import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { WifiOff, AlertTriangle, RefreshCw } from "lucide-react";

import { useOptionalToast } from "../../components/Toast/Toast";
import styles from "./NetworkBanner.module.css";

// Exponential backoff schedule (mockup L389: 1s → 2s → 5s → 10s → 30s cap).
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

type Mode = "online" | "offline" | "degraded";

/**
 * Probes a tiny endpoint to decide whether the API is reachable. We use
 * `/api/admin/auth/me` because it is cheap and always defined; a 2xx **or**
 * 401/403 both count as "API reachable" (the network round-trip succeeded).
 * Only network failures / 5xx are treated as "still degraded".
 */
async function pingApi(signal: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch("/api/admin/auth/me", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal,
    });
    return res.status < 500;
  } catch {
    return false;
  }
}

export function NetworkBanner() {
  const queryClient = useQueryClient();
  const toast = useOptionalToast();

  const [offline, setOffline] = useState<boolean>(
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );
  const [degraded, setDegraded] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);

  // Mutable refs so the QueryCache subscription doesn't re-bind on every change.
  const degradedRef = useRef(degraded);
  degradedRef.current = degraded;
  const offlineRef = useRef(offline);
  offlineRef.current = offline;

  const backoffStep = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const recover = useCallback(() => {
    clearTimer();
    backoffStep.current = 0;
    if (degradedRef.current) {
      setDegraded(false);
      toast?.push({
        variant: "success",
        message: "Reconnected. Data refreshed.",
      });
      queryClient.invalidateQueries();
    }
  }, [clearTimer, toast, queryClient]);

  // ── Auto-retry loop for the degraded state ────────────────────────────────
  const runProbe = useCallback(async () => {
    if (!degradedRef.current || offlineRef.current) return;
    setRetrying(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const ok = await pingApi(ctrl.signal);
    abortRef.current = null;
    setRetrying(false);
    if (ctrl.signal.aborted) return;
    if (ok) {
      recover();
    } else {
      scheduleNextProbe();
    }
  }, [recover]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleNextProbe = useCallback(() => {
    const idx = Math.min(backoffStep.current, BACKOFF_MS.length - 1);
    const delay = BACKOFF_MS[idx];
    backoffStep.current += 1;
    timerRef.current = setTimeout(() => {
      void runProbe();
    }, delay);
  }, [runProbe]);

  // Start/stop the loop when degraded toggles.
  useEffect(() => {
    if (degraded && !offline) {
      backoffStep.current = 0;
      scheduleNextProbe();
    } else {
      clearTimer();
    }
    return clearTimer;
  }, [degraded, offline, scheduleNextProbe, clearTimer]);

  // ── Browser online / offline events ──────────────────────────────────────
  useEffect(() => {
    function goOnline() {
      setOffline(false);
    }
    function goOffline() {
      setOffline(true);
    }
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // ── TanStack Query: detect API failures (≥ 2 consecutive) ────────────────
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      if (event.type !== "updated") return;
      const state = event.query.state;
      if (
        state.status === "error" &&
        state.fetchFailureCount >= 2 &&
        !degradedRef.current
      ) {
        setDegraded(true);
      } else if (state.status === "success" && degradedRef.current) {
        // Any successful query → API is reachable again.
        recover();
      }
    });
    return unsubscribe;
  }, [queryClient, recover]);

  // ── Manual retry now ─────────────────────────────────────────────────────
  const onRetryNow = useCallback(() => {
    if (offlineRef.current) {
      // Can't probe the API while the browser itself is offline; force a
      // refetch in case the browser flag is stale.
      queryClient.refetchQueries();
      return;
    }
    clearTimer();
    backoffStep.current = 0;
    void runProbe();
  }, [clearTimer, queryClient, runProbe]);

  // ── Decide which banner to show ──────────────────────────────────────────
  const mode: Mode = offline ? "offline" : degraded ? "degraded" : "online";
  if (mode === "online") return null;

  const isOffline = mode === "offline";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`${styles.banner} ${
        isOffline ? styles.offline : styles.degraded
      }`}
    >
      <span className={styles.icon} aria-hidden="true">
        {isOffline ? <WifiOff size={14} /> : <AlertTriangle size={14} />}
      </span>
      {isOffline ? (
        <>
          <span className={styles.title}>You&apos;re offline.</span>
          <span className={styles.meta}>
            Recent changes may not save until your connection returns.
          </span>
        </>
      ) : (
        <>
          <span className={styles.title}>Can&apos;t reach Social Hub API.</span>
          <span className={styles.meta}>
            Some actions are disabled. Showing last known data.
          </span>
        </>
      )}
      <span className={styles.spacer} />
      <button
        type="button"
        className={styles.retry}
        onClick={onRetryNow}
        disabled={retrying}
      >
        <RefreshCw
          size={12}
          aria-hidden="true"
          style={retrying ? { animation: "spin 0.8s linear infinite" } : undefined}
        />
        {retrying ? "Retrying…" : "Retry now"}
      </button>
    </div>
  );
}
